import { useState, useEffect } from 'react';
import { GlassCard, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import {
  Users,
  CheckSquare,
  Clock,
  TrendingUp,
  ArrowUpRight,
  AlertTriangle,
  CheckCircle2,
  Timer,
  Plus
} from 'lucide-react';
import { staffService, taskService, attendanceService, Task, Staff } from '@/lib/storage';
import { Link } from 'react-router-dom';
import { CreateTaskDialog } from '@/components/CreateTaskDialog';
import { getLocalDateString } from '@/lib/utils';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalStaff: 0,
    totalTasks: 0,
    completedTasks: 0,
    pendingTasks: 0,
    inProgressTasks: 0,
    presentToday: 0,
    lateToday: 0,
  });
  const [recentTasks, setRecentTasks] = useState<Task[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);

  const loadDashboardData = async () => {
    try {
      const today = getLocalDateString();
      const [staff, tasks, todayAttendance] = await Promise.all([
        staffService.getAll(),
        taskService.getAll(),
        attendanceService.getByDate(today)
      ]);

      setStaffList(staff);
      setRecentTasks(tasks.slice(-5).reverse());
      setStats({
        totalStaff: staff.length,
        totalTasks: tasks.length,
        completedTasks: tasks.filter(t => t.status === 'Completed').length,
        pendingTasks: tasks.filter(t => t.status === 'Pending').length,
        inProgressTasks: tasks.filter(t => t.status === 'In Progress').length,
        presentToday: todayAttendance.filter(a => a.status === 'present').length,
        lateToday: todayAttendance.filter(a => a.status === 'late').length,
      });
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const getStaffName = (staffId: string) => {
    const staff = staffList.find(s => s.id === staffId);
    return staff?.name || 'Unassigned';
  };

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'P0': return 'priority_high';
      case 'P1': return 'priority_medium';
      case 'P2': return 'priority_low';
      default: return 'secondary';
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'Pending': return 'status_pending';
      case 'In Progress': return 'status_progress';
      case 'Completed': return 'status_completed';
      default: return 'secondary';
    }
  };

  const completionRate = stats.totalTasks > 0
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
    : 0;

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 25 } },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="space-y-6"
    >
      {/* Top Welcome Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Overview</h2>
          <p className="text-sm text-slate-500">Real-time attendance & task execution statistics.</p>
        </div>
        <div className="flex gap-2.5 w-full sm:w-auto">
          <Button
            variant="royal"
            size="default"
            className="rounded-xl w-full sm:w-auto flex items-center justify-center gap-2 shadow-lg shadow-primary/10"
            onClick={() => setIsTaskDialogOpen(true)}
          >
            <Plus className="w-4 h-4" /> Add New Task
          </Button>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div variants={itemVariants}>
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl group overflow-hidden relative">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Staff</p>
                  <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{stats.totalStaff}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <Users className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/50 flex justify-between items-center">
                <Link to="/admin/staff" className="text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1 hover:underline">
                  Manage Staff <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </CardContent>
          </GlassCard>
        </motion.div>

        <motion.div variants={itemVariants}>
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl group overflow-hidden relative">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Tasks</p>
                  <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{stats.totalTasks}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-450 group-hover:scale-110 transition-transform">
                  <CheckSquare className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/50 flex items-center gap-3 text-xs font-medium text-slate-500">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{stats.completedTasks} Done</span>
                <span className="text-slate-300 dark:text-slate-700">|</span>
                <span className="text-blue-600 dark:text-blue-400 font-semibold">{stats.inProgressTasks} In Progress</span>
              </div>
            </CardContent>
          </GlassCard>
        </motion.div>

        <motion.div variants={itemVariants}>
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl group overflow-hidden relative">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Completion Rate</p>
                  <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{completionRate}%</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform">
                  <TrendingUp className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-5">
                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${completionRate}%` }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 shadow-md shadow-emerald-500/10"
                  />
                </div>
              </div>
            </CardContent>
          </GlassCard>
        </motion.div>

        <motion.div variants={itemVariants}>
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 shadow-sm hover:shadow-md transition-all duration-300 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl group overflow-hidden relative">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Present Today</p>
                  <p className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">{stats.presentToday + stats.lateToday}</p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 dark:bg-amber-500/20 flex items-center justify-center text-amber-600 dark:text-amber-400 group-hover:scale-110 transition-transform">
                  <Clock className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/50 flex items-center gap-3 text-xs font-medium text-slate-500">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{stats.presentToday} On Time</span>
                {stats.lateToday > 0 && (
                  <>
                    <span className="text-slate-300 dark:text-slate-700">|</span>
                    <span className="text-amber-600 dark:text-amber-400 font-semibold">{stats.lateToday} Late</span>
                  </>
                )}
              </div>
            </CardContent>
          </GlassCard>
        </motion.div>
      </div>

      {/* Main Panels Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions Panel */}
        <motion.div variants={itemVariants} className="lg:col-span-1">
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
            <CardHeader className="pb-3 pt-6 px-6">
              <CardTitle className="text-lg font-bold text-slate-900 dark:text-white">Quick Access</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-6 pb-6">
              <Button variant="royal" className="w-full h-11 justify-start rounded-xl font-semibold shadow-md shadow-primary/5" asChild>
                <Link to="/admin/staff">
                  <Users className="w-4.5 h-4.5 mr-2.5" />
                  Add New Staff Member
                </Link>
              </Button>
              <Button variant="royal" className="w-full h-11 justify-start rounded-xl font-semibold shadow-md shadow-primary/5" onClick={() => setIsTaskDialogOpen(true)}>
                <Plus className="w-4.5 h-4.5 mr-2.5" />
                Create Campaign Task
              </Button>
              <Button variant="outline" className="w-full h-11 justify-start rounded-xl font-semibold hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border-slate-200 dark:border-slate-800" asChild>
                <Link to="/admin/tasks">
                  <CheckSquare className="w-4.5 h-4.5 mr-2.5" />
                  Manage Active Tasks
                </Link>
              </Button>
              <Button variant="outline" className="w-full h-11 justify-start rounded-xl font-semibold hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border-slate-200 dark:border-slate-800" asChild>
                <Link to="/admin/attendance">
                  <Clock className="w-4.5 h-4.5 mr-2.5" />
                  Track Attendance Logs
                </Link>
              </Button>
              <Button variant="outline" className="w-full h-11 justify-start rounded-xl font-semibold hover:bg-slate-100/50 dark:hover:bg-slate-800/40 border-slate-200 dark:border-slate-800" asChild>
                <Link to="/admin/reports">
                  <TrendingUp className="w-4.5 h-4.5 mr-2.5" />
                  View Activity Reports
                </Link>
              </Button>
            </CardContent>
          </GlassCard>
        </motion.div>

        {/* Task Overview & Recent Activity */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl shadow-sm bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
            <CardHeader className="pb-3 pt-6 px-6 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-bold text-slate-900 dark:text-white">Campaign Tasks</CardTitle>
              <Link to="/admin/tasks" className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                View All Tasks
              </Link>
            </CardHeader>
            <CardContent className="px-6 pb-6">
              {/* Task Breakdown Stats */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="p-3.5 rounded-2xl bg-amber-500/5 dark:bg-amber-500/10 border border-amber-200/10 text-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto mb-1.5" />
                  <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{stats.pendingTasks}</p>
                  <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">Pending</p>
                </div>
                <div className="p-3.5 rounded-2xl bg-blue-500/5 dark:bg-blue-500/10 border border-blue-200/10 text-center">
                  <Timer className="w-5 h-5 text-blue-500 mx-auto mb-1.5" />
                  <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{stats.inProgressTasks}</p>
                  <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">In Progress</p>
                </div>
                <div className="p-3.5 rounded-2xl bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-200/10 text-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto mb-1.5" />
                  <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{stats.completedTasks}</p>
                  <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mt-1.5">Completed</p>
                </div>
              </div>

              {/* Recent Tasks List */}
              <div className="space-y-2.5">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Recently Added</p>
                {recentTasks.length === 0 ? (
                  <div className="text-center py-8 border border-dashed border-slate-200 dark:border-slate-800 rounded-2xl">
                    <p className="text-sm text-slate-400 font-medium">No tasks found. Create a task to get started!</p>
                  </div>
                ) : (
                  recentTasks.map((task) => (
                    <motion.div
                      key={task.id}
                      whileHover={{ scale: 1.005 }}
                      className="flex items-center justify-between p-4 rounded-2xl border border-slate-150 dark:border-slate-800/80 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-200"
                    >
                      <div className="flex-1 min-w-0 pr-4">
                        <p className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400 font-medium">
                          <span>Assigned to:</span>
                          <span className="text-slate-600 dark:text-slate-300 font-semibold">
                            {task.assignedStaff && task.assignedStaff.length > 0 
                              ? task.assignedStaff.map(id => getStaffName(id)).join(', ') 
                              : getStaffName(task.assignedTo || '')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getPriorityVariant(task.priority) as any} className="rounded-lg px-2 py-0.5 text-[10px] font-bold">
                          {task.priority}
                        </Badge>
                        <Badge variant={getStatusVariant(task.status) as any} className="rounded-lg px-2 py-0.5 text-[10px] font-bold">
                          {task.status}
                        </Badge>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </CardContent>
          </GlassCard>
        </motion.div>
      </div>

      <CreateTaskDialog 
        isOpen={isTaskDialogOpen} 
        onOpenChange={setIsTaskDialogOpen} 
        onSuccess={loadDashboardData}
      />
    </motion.div>
  );
};

export default AdminDashboard;
